import { useEffect } from "react";
import { useI18n } from "@/i18n";
import { useSeo, absAsset } from "@/lib/seo";

function setTag(selector, create, attr, value) {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = create();
    document.head.appendChild(el);
  }
  el.setAttribute(attr, value);
}

const meta = (key, prop = "name") => () => {
  const m = document.createElement("meta");
  m.setAttribute(prop, key);
  return m;
};

// Sets <title>, description, keywords, canonical and Open Graph tags.
// Priority: admin page-level SEO (per language) → the page's built-in text → admin site-wide defaults.
export function useMeta(title, description, { image, type = "website", page } = {}) {
  const seo = useSeo();
  const { lang } = useI18n();
  const pm = (page && seo.pages?.[page]?.[lang]) || {};
  const site = (k) => seo[k]?.[lang] || "";
  const siteTitle = site("title");
  const fTitle = pm.title || title || siteTitle || "woodesing";
  const fDesc = pm.description || description || site("description");
  const fKeys = pm.keywords || site("keywords");
  const fImage = absAsset(pm.og_image) || image || absAsset(seo.og_image);

  useEffect(() => {
    const base = seo.site_url || window.location.origin;
    const url = base + window.location.pathname;
    document.title = fTitle;
    setTag('meta[name="description"]', meta("description"), "content", fDesc);
    if (fKeys) setTag('meta[name="keywords"]', meta("keywords"), "content", fKeys);
    else document.head.querySelector('meta[name="keywords"]')?.remove();
    setTag('meta[property="og:title"]', meta("og:title", "property"), "content", fTitle);
    setTag('meta[property="og:description"]', meta("og:description", "property"), "content", fDesc);
    setTag('meta[property="og:type"]', meta("og:type", "property"), "content", type);
    setTag('meta[property="og:url"]', meta("og:url", "property"), "content", url);
    setTag('meta[property="og:site_name"]', meta("og:site_name", "property"), "content", siteTitle || "woodesing");
    if (fImage) setTag('meta[property="og:image"]', meta("og:image", "property"), "content", fImage);
    setTag('link[rel="canonical"]', () => { const l = document.createElement("link"); l.rel = "canonical"; return l; }, "href", url);
    return () => {
      document.title = siteTitle || "woodesing";
      document.head.querySelector('meta[property="og:image"]')?.remove();
    };
  }, [fTitle, fDesc, fKeys, fImage, type, seo.site_url, siteTitle]);
}
